// @ts-strict-ignore
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDebounce, useFeatureFlags } from '@node-space/hooks'
import { Box, Button, Input } from '@node-space/ui-components'
import { PathNames, SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { BeneficiaryFilters, useGetBeneficiaries } from 'hooks/fiat/useFiatBeneficiaries'
import { BeneficiaryDetails } from 'types/beneficiaries'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { getFiatSendBeneficiaryFilters } from 'utils/beneficiaries'
import track from 'utils/tracker'
import {
  fiatSendFormAtom,
  selectedContactBankAccountAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from '../../Pay/atoms/atoms'
import useBeneficiaryFormContext from '../hooks/useBeneficiaryFormContext'
import { useWalletBeneficiaryFilters } from '../hooks/useWalletBeneficiaryFilters'
import { useWalletOptions } from '../hooks/useWalletOptions'
import BeneficiaryContent from './BeneficiaryContent'
import { ErrorStateDisplay } from './shared/ErrorStateDisplay'
import { WalletSelector } from './shared/WalletSelector'

interface BeneficiaryFormProps {
  setStep: React.Dispatch<React.SetStateAction<FiatSendStep>>
  isLoadingWallet: boolean
}

const BeneficiaryForm = ({ setStep, isLoadingWallet }: BeneficiaryFormProps) => {
  const { t } = useTranslation()
  const { enableLegacyBeneficiaryCreation } = useFeatureFlags()
  const navigate = useNavigate()

  const sourceWalletAtom = useAtomValue(selectedWalletAtom)
  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setContactBankAccountAtom = useSetAtom(selectedContactBankAccountAtom)
  const formAtom = useAtomValue(fiatSendFormAtom)

  const [showErrorState, _setShowErrorState] = useState(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [beneficiaryFilters, setBeneficiaryFilters] = useState<BeneficiaryFilters>()
  const debouncedSearchTerm: string = useDebounce(searchQuery, SEARCH_INPUT_DEBOUNCE_TIME)

  const { errors, clearErrors, register, setValue, setError, resetField } = formAtom || {}

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_BENEFICIARY, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const {
    isLoading: isFetchingWalletFilters,
    filters,
    capabilities,
  } = useWalletBeneficiaryFilters({
    wallet: sourceWalletAtom,
  })

  const { isInsufficientBalance, isPayoutsSupported } =
    useBeneficiaryFormContext({
      wallet: sourceWalletAtom,
      filters,
    })

  const { walletsOptions } = useWalletOptions({
    sourceWalletAtom: sourceWalletAtom,
  })

  const { fiatBeneficiaries, isFetching: isFetchingBeneficiaries } = useGetBeneficiaries({
    filters: beneficiaryFilters,
    capabilities,
  })

  useEffect(() => {
    resetField('amount')
    resetField('fee')
    resetField('paymentReference')
    resetField('beneficiaryReference')
  }, [])

  useEffect(() => {
    if (!sourceWalletAtom || isFetchingWalletFilters) return

    if (sourceWalletAtom?.balance > 0) {
      if (filters?.length) {
        const beneficiariesFilter = getFiatSendBeneficiaryFilters({
          sourceWallet: sourceWalletAtom,
          supportedFilters: filters?.map(filter => filter?.name),
        })
        setBeneficiaryFilters({
          ...beneficiariesFilter,
          beneficiarySearchTerm: debouncedSearchTerm,
        })
      }
    }
  }, [sourceWalletAtom, isFetchingWalletFilters, debouncedSearchTerm])

  const isLoadingWalletOptions = useMemo(() => {
    return isLoadingWallet
  }, [isLoadingWallet])

  const handleBeneficiaryFilterChange = useCallback(
    (selectedFilter: string[]) => {
      const beneficiariesFilter = getFiatSendBeneficiaryFilters({
        selectedFilter: selectedFilter?.[0],
        sourceWallet: sourceWalletAtom,
        supportedFilters: filters?.map(filter => filter?.name),
      })

      setBeneficiaryFilters({ ...beneficiariesFilter, beneficiarySearchTerm: debouncedSearchTerm })
    },
    [sourceWalletAtom, filters]
  )

  const handleBeneficiarySelect = useCallback(
    (beneficiary: BeneficiaryDetails) => {
      if (!sourceWalletAtom) {
        setError('sourceWalletLsid', { message: t('wallets.fiatSend.noWalletSelectedError') })
        return
      }
      clearErrors()
      setValue('beneficiaryReference', beneficiary?.reference)
      setValue('currencyCode', beneficiary?.currency)
      setValue('transferDestination', beneficiary?.transferDestination)
      setBeneficiaryAtom(beneficiary)
      setContactBankAccountAtom(null)
      setValue('paymentInstrumentId', '')
      setValue('paymentMethod', '')
      setValue('achSameDay', false)      
      setStep(FiatSendSteps.DETAILS)

      track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_SELECT_BENEFICIARY, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.CLICK,
        beneficiaryDetails: {
          entityType: beneficiary?.entityType,
          reference: beneficiary?.reference,
          currency: beneficiary?.currency,
          transferDestination: beneficiary?.transferDestination,
          paymentMethod: beneficiary?.paymentMethod,
        },
        beneficiaryFilters,
      })
    },
    [
      sourceWalletAtom,
      beneficiaryFilters,
      setError,
      clearErrors,
      setValue,
      setBeneficiaryAtom,
      setStep,
      t,
    ]
  )

  const onHandleBeneficiarySearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    [setSearchQuery]
  )

  const handleOnAddBeneficiaryClick = useCallback(() => {
    track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_BENEFICIARY_ADD, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.NAVIGATE,
    })
    navigate(PathNames.BANK_BENEFICIARIES)
  }, [navigate])


  const filtersDisabled = useMemo(() => {
    return (
      !isLoadingWalletOptions &&
      !isFetchingWalletFilters &&
      !isFetchingBeneficiaries &&
      (!isPayoutsSupported || isInsufficientBalance)
    )
  }, [
    !isLoadingWalletOptions,
    isFetchingWalletFilters,
    isFetchingBeneficiaries,
    isPayoutsSupported,
    isInsufficientBalance,
    fiatBeneficiaries?.length,
  ])

  return (
    <Box paddingT={24} className="max-h-[75vh]">
      {showErrorState && <ErrorStateDisplay />}
      {!showErrorState && (
        <Box paddingB={16}>
          <WalletSelector
            register={register('sourceWalletLsid')}
            sourceWalletAtom={sourceWalletAtom}
            walletsOptions={walletsOptions}
            isLoadingWalletOptions={isLoadingWalletOptions}
            errorText={errors?.sourceWalletLsid?.message}
          />

          {sourceWalletAtom && (
            <>
              <Box paddingY={16} width="full" justify="between" flex gapY={12} gapX={12}>
                <Box width="full">
                  <Input
                    placeholderText={t('wallets.fiatSend.search')}
                    onChange={onHandleBeneficiarySearch}
                    searchable={true}
                    testId="fiat-send-beneficiary-search"
                    helperText={t('payouts.beneficiaryFiltersDisclaimer')}
                    disabled={filtersDisabled}
                  />
                </Box>
                {enableLegacyBeneficiaryCreation && <Button
                  testId="add-contact"
                  variant="secondary"
                  onClick={handleOnAddBeneficiaryClick}
                >
                  {t('addNewBeneficiaryWithoutPlus')}
                </Button>}
              </Box>

              <BeneficiaryContent
                fiatBeneficiaries={fiatBeneficiaries}
                filters={filters}
                isFetchingBeneficiaries={isFetchingBeneficiaries}
                isFetchingFilters={isFetchingWalletFilters}
                isInsufficientBalance={isInsufficientBalance}
                isPayoutsSupported={isPayoutsSupported}
                sourceWalletAtom={sourceWalletAtom}
                handleBeneficiaryFilterChange={handleBeneficiaryFilterChange}
                handleBeneficiarySelect={handleBeneficiarySelect}
                setSearchQuery={setSearchQuery}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  )
}

export default memo(BeneficiaryForm)
